import 'styles/globals.css'
import { Analytics } from '@vercel/analytics/react'
import { SpeedInsights } from '@vercel/speed-insights/react'
import { useRouter } from 'next/router'
import { appWithTranslation } from 'next-i18next'
import { MixpanelProvider } from '@captaincause/lib/mixpanel'
import { AppLayout } from 'layouts/App'
import { Portals } from 'layouts/Portals'
import { WidgetProvider } from 'utils/widget/Provider'
import { CookiesConsent } from 'components/Cookies'
import { EmbedProvider } from 'components/Embed'
import ErrorBoundary from 'components/ErrorBoundary'
import type { LayoutProps } from 'layouts/App'
import type { NextPage } from 'next'
import type { AppProps } from 'next/app'
import type { SSRConfig } from 'next-i18next'

interface InitialPageProps {
  _nextI18Next?: SSRConfig
  fonts?: number
}

export type NextPageWithLayout<P = {}, IP = P> = NextPage<
  P & InitialPageProps,
  IP & InitialPageProps
> & {
  Layout?: ((props: LayoutProps) => JSX.Element) | null
  pagePropsOnLayout?: boolean
  fonts?: string
}

type AppPropsWithLayout = AppProps<InitialPageProps> & {
  Component: NextPageWithLayout<InitialPageProps>
}

function App({ Component, pageProps }: AppPropsWithLayout) {
  const router = useRouter()

  const PageLayout = Component.Layout ?? null
  const layoutPageProps = Component.pagePropsOnLayout ? pageProps : undefined

  return (
    <ErrorBoundary>
      <MixpanelProvider
        token={process.env.NEXT_PUBLIC_MIXPANEL_TOKEN}
        apiHost={process.env.NEXT_PUBLIC_MIXPANEL_TUNNEL}
        config={{ disable_persistence: false, persistence: 'localStorage' }}
      >
        <EmbedProvider>
          <WidgetProvider>
            <AppLayout Component={PageLayout} pageProps={layoutPageProps}>
              <>
                <Component {...pageProps} />
                <Analytics />
                <SpeedInsights route={router.pathname} sampleRate={0.1} />
              </>
            </AppLayout>
          </WidgetProvider>
          <Portals />
          <CookiesConsent />
        </EmbedProvider>
      </MixpanelProvider>
    </ErrorBoundary>
  )
}

export default appWithTranslation(App)
